// src/cluster/components/form/hooks/useForm.ts (веб)

import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ClusterPayload,
  ClusterPaymentSettings,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { clusterValidationsForm } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { ClusterFormView, ClusterTypes } from '../../../../../../common/types/enums'
import { getApi } from '../../../../../../api/api'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '../../../../../../store/slices/currentUserSlice'
import { chatAdminId } from '../../../../../../common/utils/constants'

const validations: ValidationRulesInterface[] = clusterValidationsForm

export const useForm = (
  data: ClusterPayload,
  setData: React.Dispatch<SetStateAction<ClusterPayload>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: ClusterPayload,
) => {
  const [api] = useState(getApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const [view, setView] = useState<ClusterFormView>(ClusterFormView.FORM)
  const [settingsDisabled, setSettingsDisabled] = useState(false)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const currentUser = useSelector(selectCurrentUser)

  useEffect(() => {
    if (el) setData(el)
  }, [el, setData])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)
      if (data.id) {
        const {
          createdAt,
          updatedAt,
          token,
          port,
          authPrimary,
          collectEmail,
          registeredUsersCount,
          billing,
          ...rest
        } = data

        const payload: any = {
          ...rest,
          delivery: rest.delivery,
          bonuses: rest.bonuses,
        }

        if (currentUser?.id === chatAdminId && data.tariff?.name) {
          payload.tariff = data.tariff
        }

        await api.updateCluster(payload)
      }
      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [api, data, getList, setModalActive, showCommonLoader, hideCommonLoader, currentUser])

  const toggleAction = useCallback(
    (fieldName: string) => {
      setData((prev) => {
        const newData = { ...prev, [fieldName]: !prev[fieldName] }

        if (fieldName === 'withBonuses' && !newData.withBonuses) {
          newData.writeOff = ''
          newData.bonuses = []
        }

        // ❌ выключили доставку — сбрасываем автозадания
        if (fieldName === 'withDelivery' && !newData.withDelivery) {
          newData.autoCreateDeliveryTasks = false
        }

        if (fieldName === 'forceStore' && !newData.forceStore) {
          newData.onePrice = true
        }

        return newData
      })
    },
    [setData],
  )

  const cdekHandler = useCallback(
    (fieldName: 'enabled' | 'insurance' | 'pickup' | 'courier') => {
      setData((prev) => {
        const currentCdek = prev.cdek || {
          enabled: false,
          insurance: false,
          markup: 0,
          pickup: false,
          courier: false,
        }

        return {
          ...prev,
          cdek: {
            ...currentCdek,
            [fieldName]: !currentCdek[fieldName],
          },
        }
      })
    },
    [setData],
  )

  const cdekMarkupHandler = useCallback(
    (value: string | number | boolean | (string | undefined)[]) => {
      setData((prev) => {
        const currentCdek = prev.cdek || {
          enabled: false,
          insurance: false,
          markup: 0,
          pickup: false,
          courier: false,
        }

        return {
          ...prev,
          cdek: {
            ...currentCdek,
            markup: typeof value === 'string' ? value : String(value),
          },
        }
      })
    },
    [setData],
  )

  const paymentHandler = useCallback(
    (fieldName: keyof ClusterPaymentSettings) => {
      setData((prev) => ({
        ...prev,
        payment: {
          ...prev.payment,
          [fieldName]: !prev.payment?.[fieldName],
        },
      }))
    },
    [setData],
  )

  const toggleHandler = useCallback(
    (paymentType: 'yookassa' | 'paymaster') => {
      setData((prev) => ({
        ...prev,
        yookassa: {
          ...prev.yookassa,
          enabled: paymentType === 'yookassa' ? !prev.yookassa?.enabled : false,
        },
        paymaster: {
          ...prev.paymaster,
          enabled: paymentType === 'paymaster' ? !prev.paymaster?.enabled : false,
        },
      }))
    },
    [setData],
  )

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    if (Number(data.type) !== ClusterTypes.FOOD && data.cutlery) {
      setData((prev) => ({ ...prev, cutlery: false }))
    }
  }, [data.type, data.cutlery, setData])

  const isButtonDisabled = useMemo(() => {
    if (view === ClusterFormView.FORM) {
      const hasGeneralErrors = validationResult.some((el) => el.notValid && el.name !== 'writeOff')

      const writeOffInvalid =
        data.withBonuses && validationResult.some((el) => el.name === 'writeOff' && el.notValid)

      const bonusesEmpty = data.withBonuses && (!data.bonuses || data.bonuses.length === 0)

      const hasBonusErrors =
        data.withBonuses && (settingsDisabled || writeOffInvalid || bonusesEmpty)

      return Boolean(hasGeneralErrors || hasBonusErrors)
    }

    if (view === ClusterFormView.DELIVERY) {
      const hasDeliveryErrors =
        data.delivery?.some((item) => {
          const isValueEmpty = item.value === undefined || item.value === null || item.value === ''
          return isValueEmpty
        }) ?? false

      const hasValidationErrors = validationResult.some(
        (el) => el.notValid && el.name.includes('delivery'),
      )

      return hasDeliveryErrors || hasValidationErrors
    }

    if (view === ClusterFormView.CDEK) {
      const markup = data.cdek?.markup

      const markupInvalid =
        markup !== undefined &&
        markup !== '' &&
        (!/^-?\d{1,3}$/.test(String(markup)) || Number(markup) > 999 || Number(markup) < -100)

      return markupInvalid
    }

    if (view === ClusterFormView.BONUS) {
      if (!data.withBonuses) return false

      const hasBonusErrors =
        data.bonuses?.some((item) => {
          const isLevelEmpty = item.level === undefined || item.level === null || item.level === ''
          const isValueEmpty = item.value === undefined || item.value === null || item.value === ''
          return isLevelEmpty || isValueEmpty
        }) ?? false

      const writeOffError = validationResult.some((el) => el.name === 'writeOff' && el.notValid)
      const bonusesEmpty = !data.bonuses || data.bonuses.length === 0

      return Boolean(writeOffError || settingsDisabled || bonusesEmpty || hasBonusErrors)
    }

    if (view === ClusterFormView.PAYMENT) {
      return false
    }

    if (view === ClusterFormView.TARIFF) {
      const tariffErrors = validationResult.some(
        (el) => el.notValid && ['name', 'basePrice', 'specialPrice'].includes(el.name),
      )
      return tariffErrors || settingsDisabled
    }

    return false
  }, [
    view,
    validationResult,
    settingsDisabled,
    data.delivery,
    data.withBonuses,
    data.bonuses,
    data.cdek,
  ])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    toggleAction,
    view,
    setView,
    setSettingsDisabled,
    toggleHandler,
    isButtonDisabled,
    paymentHandler,
    cdekHandler,
    cdekMarkupHandler,
  }
}
